import { currentYear as siteYear } from "@/lib/siteYear";
export interface ProductCategoryConfig {
  title: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  description: string;
  longDescription: string;
  heroGradient: string;
  /** Keywords used to match articles from the articles table */
  articleKeywords: string[];
  /** If this category maps to a DB product table, specify its path prefix */
  productTablePath?: string;
}

const currentYear = siteYear();

export const productCategories: Record<string, ProductCategoryConfig> = {
  lawnmowers: {
    title: "Lawnmowers",
    slug: "lawnmowers",
    metaTitle: `Best Lawnmower Reviews UK ${currentYear} | Expert Tested & Rated`,
    metaDescription: `In-depth UK lawnmower reviews. Push, petrol, cordless, robotic & ride-on mowers tested by gardening experts. Find the best mower for your garden.`,
    description: "Expert reviews of push, petrol, cordless, robotic and ride-on lawnmowers — all independently tested in real UK gardens.",
    longDescription: "Our expert reviewers independently test every lawnmower we feature. Whether you have a small city garden or a large country lawn, we help you find the perfect mower with honest ratings, performance data, and buying advice tailored to UK conditions.",
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20",
    articleKeywords: ["lawn mower", "lawnmower", "mower", "mowing"],
    productTablePath: "/lawn-mowers",
  },
  scarifiers: {
    title: "Scarifiers",
    slug: "scarifiers",
    metaTitle: `Best Scarifier Reviews UK ${currentYear} | Expert Tested & Rated`,
    metaDescription: `Expert UK scarifier reviews. Electric, petrol and manual scarifiers tested to help you remove thatch and moss for a healthier lawn.`,
    description: "Reviews of electric, petrol and manual scarifiers to help you remove thatch and moss for a healthier, greener lawn.",
    longDescription: "Scarifying is essential for a healthy UK lawn. Our team tests scarifiers in real garden conditions, assessing dethatching performance, build quality, and value for money to help you choose the right one.",
    heroGradient: "bg-linear-to-br from-lime-50 to-green-50 dark:from-lime-950/20 dark:to-green-950/20",
    articleKeywords: ["scarifier", "scarifying", "dethatcher", "thatch"],
  },
  "grass-trimmers-brushcutters": {
    title: "Grass Trimmers & Brushcutters",
    slug: "grass-trimmers-brushcutters",
    metaTitle: `Best Grass Trimmer & Brushcutter Reviews UK ${currentYear}`,
    metaDescription: `Expert UK reviews of grass trimmers, strimmers and brushcutters. Cordless, petrol and electric models tested for performance and value.`,
    description: "Reviews of grass trimmers, strimmers and brushcutters — cordless, petrol and electric models tested for edging and clearing.",
    longDescription: "From lightweight cordless trimmers for neat edges to powerful petrol brushcutters for tough overgrowth, we test every model in real UK garden conditions to help you find the right tool.",
    heroGradient: "bg-linear-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20",
    articleKeywords: ["grass trimmer", "strimmer", "brushcutter", "trimmer", "strimmers"],
  },
  "leaf-blowers": {
    title: "Leaf Blowers",
    slug: "leaf-blowers",
    metaTitle: `Best Leaf Blower Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `In-depth UK leaf blower reviews. Cordless, petrol and electric leaf blowers & garden vacuums tested and rated by experts.`,
    description: "Expert reviews of cordless, petrol and electric leaf blowers and garden vacuums, tested in real UK gardens.",
    longDescription: "Whether you need a lightweight cordless blower for patios or a powerful backpack model for large gardens, our experts test every leaf blower for power, battery life, and noise levels in UK conditions.",
    heroGradient: "bg-linear-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20",
    articleKeywords: ["leaf blower", "garden vacuum", "leaf vac", "blower vac"],
  },
  chainsaws: {
    title: "Chainsaws",
    slug: "chainsaws",
    metaTitle: `Best Chainsaw Reviews UK ${currentYear} | Expert Tested & Rated`,
    metaDescription: `Expert UK chainsaw reviews. Petrol, cordless and electric chainsaws tested for cutting performance, safety and value for money.`,
    description: "Reviews of petrol, cordless and electric chainsaws — tested for cutting performance, safety features and value.",
    longDescription: "Our chainsaw reviews cover everything from compact pruning saws to professional-grade petrol models. Every chainsaw is tested on real wood in UK conditions, with honest assessments of cutting speed, safety, and ease of use.",
    heroGradient: "bg-linear-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20",
    articleKeywords: ["chainsaw", "chain saw"],
    productTablePath: "/chainsaws",
  },
  "log-splitters": {
    title: "Log Splitters",
    slug: "log-splitters",
    metaTitle: `Best Log Splitter Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK log splitter reviews. Electric, hydraulic and manual log splitters tested for splitting force, speed and value.`,
    description: "Reviews of electric, hydraulic and manual log splitters tested for splitting force, speed and value for money.",
    longDescription: "If you burn logs, a good log splitter saves hours of manual work. We test electric and hydraulic models on seasoned and green wood to give you honest performance data and buying recommendations.",
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50 dark:from-stone-950/20 dark:to-amber-950/20",
    articleKeywords: ["log splitter", "wood splitter", "splitting"],
  },
  shredders: {
    title: "Shredders",
    slug: "shredders",
    metaTitle: `Best Garden Shredder Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK garden shredder reviews. Electric and petrol shredders tested for chipping performance, noise levels and ease of use.`,
    description: "Reviews of electric and petrol garden shredders tested for chipping performance, noise levels and ease of use.",
    longDescription: "Garden shredders turn bulky prunings into useful mulch and compost material. We test impact and roller shredders on real garden waste, assessing throughput speed, noise, and jam resistance.",
    heroGradient: "bg-linear-to-br from-green-50 to-lime-50 dark:from-green-950/20 dark:to-lime-950/20",
    articleKeywords: ["shredder", "chipper", "garden shredder"],
  },
  "pressure-washers": {
    title: "Pressure Washers",
    slug: "pressure-washers",
    metaTitle: `Best Pressure Washer Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `In-depth UK pressure washer reviews. Electric and petrol models tested for cleaning power, build quality and value for money.`,
    description: "Expert reviews of electric and petrol pressure washers, tested for cleaning power, accessories and value.",
    longDescription: "From compact patio cleaners to powerful petrol washers for driveways and vehicles, we test every model for water pressure, flow rate, build quality, and included accessories.",
    heroGradient: "bg-linear-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20",
    articleKeywords: ["pressure washer", "jet wash", "power washer"],
    productTablePath: "/pressure-washers",
  },
  "hedge-trimmers": {
    title: "Hedge Trimmers",
    slug: "hedge-trimmers",
    metaTitle: `Best Hedge Trimmer Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK hedge trimmer reviews. Cordless, petrol and electric hedge trimmers tested for cutting performance and ease of use.`,
    description: "Reviews of cordless, petrol and electric hedge trimmers tested for cutting performance, reach and ease of use.",
    longDescription: "Whether you have a small privet hedge or a long run of leylandii, we test hedge trimmers on real hedges to assess cutting power, blade quality, weight balance, and battery life.",
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50 dark:from-green-950/20 dark:to-teal-950/20",
    articleKeywords: ["hedge trimmer", "hedge cutter", "hedge cutting"],
  },
  "tillers-rotavators": {
    title: "Tillers & Rotavators",
    slug: "tillers-rotavators",
    metaTitle: `Best Tiller & Rotavator Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK tiller and rotavator reviews. Electric and petrol cultivators tested for digging performance and soil preparation.`,
    description: "Reviews of electric and petrol tillers and rotavators tested for digging depth, performance and soil preparation.",
    longDescription: "Preparing vegetable patches, flower beds, or new lawn areas is much easier with the right tiller. We test rotavators and cultivators on different soil types to give you reliable performance data.",
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50 dark:from-amber-950/20 dark:to-stone-950/20",
    articleKeywords: ["tiller", "rotavator", "cultivator"],
  },
  "earth-augers": {
    title: "Earth Augers",
    slug: "earth-augers",
    metaTitle: `Best Earth Auger Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK earth auger reviews. Petrol and cordless post hole diggers tested for boring performance and ease of use.`,
    description: "Reviews of petrol and cordless earth augers and post hole diggers tested for boring depth and performance.",
    longDescription: "Earth augers make quick work of digging fence post holes, planting trees, and soil sampling. We test augers on different soil types to assess boring speed, depth capability, and user comfort.",
    heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50 dark:from-stone-950/20 dark:to-zinc-950/20",
    articleKeywords: ["earth auger", "post hole digger", "auger"],
  },
  "water-pumps": {
    title: "Water Pumps",
    slug: "water-pumps",
    metaTitle: `Best Water Pump Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK water pump reviews. Submersible, dirty water and clean water pumps tested for flow rate and reliability.`,
    description: "Reviews of submersible, dirty water and clean water pumps tested for flow rate, head pressure and reliability.",
    longDescription: "From draining flooded areas to irrigating gardens, the right water pump makes a huge difference. We test pumps for flow rate, head height, priming ease, and durability.",
    heroGradient: "bg-linear-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20",
    articleKeywords: ["water pump", "submersible pump", "dirty water pump"],
  },
  generators: {
    title: "Generators",
    slug: "generators",
    metaTitle: `Best Generator Reviews UK ${currentYear} | Expert Tested & Rated`,
    metaDescription: `Expert UK generator reviews. Petrol, inverter and portable generators tested for power output, noise levels and fuel efficiency.`,
    description: "Expert reviews of petrol, inverter and portable generators tested for power output, noise and fuel efficiency.",
    longDescription: "Whether you need backup power for your home, a portable generator for camping, or a site generator for tools, our experts test every model for real-world power output, noise levels, and runtime.",
    heroGradient: "bg-linear-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/20 dark:to-amber-950/20",
    articleKeywords: ["generator", "portable generator", "inverter generator"],
    productTablePath: "/generators",
  },
  "air-compressors": {
    title: "Air Compressors",
    slug: "air-compressors",
    metaTitle: `Best Air Compressor Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK air compressor reviews. Portable, oilless and workshop compressors tested for CFM, tank size and noise levels.`,
    description: "Reviews of portable, oilless and workshop air compressors tested for CFM output, tank capacity and noise.",
    longDescription: "Air compressors power everything from spray guns and nail guns to tyre inflators. We test compressors for actual CFM delivery, recovery time, noise levels, and build quality.",
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50 dark:from-slate-950/20 dark:to-zinc-950/20",
    articleKeywords: ["air compressor", "compressor"],
  },
  "cement-mixers": {
    title: "Cement Mixers",
    slug: "cement-mixers",
    metaTitle: `Best Cement Mixer Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK cement mixer reviews. Electric and petrol cement mixers tested for drum capacity, mixing performance and portability.`,
    description: "Reviews of electric and petrol cement mixers tested for drum capacity, mixing speed and portability.",
    longDescription: "From small DIY projects to larger landscaping jobs, the right cement mixer saves time and effort. We test mixers for drum capacity, motor power, mixing quality, and ease of cleaning.",
    heroGradient: "bg-linear-to-br from-gray-50 to-stone-50 dark:from-gray-950/20 dark:to-stone-950/20",
    articleKeywords: ["cement mixer", "concrete mixer"],
  },
  welders: {
    title: "Welders",
    slug: "welders",
    metaTitle: `Best Welder Reviews UK ${currentYear} | MIG, TIG & Arc Tested`,
    metaDescription: `Expert UK welder reviews. MIG, TIG and arc welders tested for weld quality, ease of use and value for money.`,
    description: "Reviews of MIG, TIG and arc welders tested for weld quality, duty cycle, ease of use and value.",
    longDescription: "Whether you're a hobbyist or professional, choosing the right welder matters. We test MIG, TIG, and stick welders on different metals and thicknesses to assess arc stability, duty cycle, and build quality.",
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20",
    articleKeywords: ["welder", "welding", "MIG welder", "TIG welder"],
  },
  "power-tools": {
    title: "Power Tools",
    slug: "power-tools-cat",
    metaTitle: `Best Power Tool Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK power tool reviews covering drills, grinders, saws, sanders and more. All independently tested and rated.`,
    description: "Expert reviews of drills, grinders, saws, sanders and more — all independently tested and rated by our experts.",
    longDescription: "Our power tool reviews cover the full range of corded and cordless tools for DIY and professional use. Every tool is tested in real-world tasks to give you honest, reliable buying advice.",
    heroGradient: "bg-linear-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20",
    articleKeywords: ["power tool", "power tools"],
  },
  drills: {
    title: "Drills",
    slug: "drills",
    metaTitle: `Best Drill Reviews UK ${currentYear} | Cordless & Corded Tested`,
    metaDescription: `Expert UK drill reviews. Combi drills, impact drivers, hammer drills and SDS drills tested for power, speed and value.`,
    description: "Reviews of combi drills, impact drivers, hammer drills and SDS drills tested for drilling power and value.",
    longDescription: "From cordless combi drills for home DIY to professional SDS hammer drills for masonry, we test every drill for torque, speed settings, chuck quality, and battery performance.",
    heroGradient: "bg-linear-to-br from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20",
    articleKeywords: ["drill", "combi drill", "impact driver", "hammer drill", "SDS drill"],
  },
  grinders: {
    title: "Grinders",
    slug: "grinders",
    metaTitle: `Best Angle Grinder Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK angle grinder reviews. Cordless and corded grinders tested for cutting and grinding performance.`,
    description: "Reviews of cordless and corded angle grinders tested for cutting power, disc size and safety features.",
    longDescription: "Angle grinders are essential for cutting, grinding, and polishing metal, stone, and tile. We test each model for motor power, vibration, guard design, and disc compatibility.",
    heroGradient: "bg-linear-to-br from-zinc-50 to-slate-50 dark:from-zinc-950/20 dark:to-slate-950/20",
    articleKeywords: ["grinder", "angle grinder"],
  },
  saws: {
    title: "Saws",
    slug: "saws",
    metaTitle: `Best Saw Reviews UK ${currentYear} | Circular, Jig & Mitre Tested`,
    metaDescription: `Expert UK saw reviews. Circular saws, jigsaws, mitre saws and reciprocating saws tested for cutting accuracy and power.`,
    description: "Reviews of circular saws, jigsaws, mitre saws and reciprocating saws tested for cutting accuracy and power.",
    longDescription: "From precision mitre saws for trim work to rugged reciprocating saws for demolition, we test every saw for cut quality, accuracy, blade visibility, and dust extraction.",
    heroGradient: "bg-linear-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20",
    articleKeywords: ["saw", "circular saw", "jigsaw", "mitre saw", "reciprocating saw", "table saw"],
  },
  "compactor-plates": {
    title: "Compactor Plates",
    slug: "compactor-plates",
    metaTitle: `Best Compactor Plate Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK compactor plate reviews. Petrol and electric plate compactors tested for compaction force and manoeuvrability.`,
    description: "Reviews of petrol and electric plate compactors tested for compaction force, surface coverage and handling.",
    longDescription: "Compactor plates are essential for laying patios, driveways, and sub-bases. We test each model for compaction force, plate size, vibration, and ease of manoeuvring on different surfaces.",
    heroGradient: "bg-linear-to-br from-stone-50 to-neutral-50 dark:from-stone-950/20 dark:to-neutral-950/20",
    articleKeywords: ["compactor plate", "wacker plate", "plate compactor"],
  },
  "mini-dumpers": {
    title: "Mini Dumpers",
    slug: "mini-dumpers",
    metaTitle: `Best Mini Dumper Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK mini dumper reviews. Powered wheelbarrows and mini dumpers tested for load capacity and terrain handling.`,
    description: "Reviews of powered wheelbarrows and mini dumpers tested for load capacity, traction and terrain handling.",
    longDescription: "Mini dumpers and powered wheelbarrows are invaluable for moving heavy loads of soil, gravel, and materials around gardens and building sites. We test for load capacity, gradient capability, and ease of use.",
    heroGradient: "bg-linear-to-br from-yellow-50 to-stone-50 dark:from-yellow-950/20 dark:to-stone-950/20",
    articleKeywords: ["mini dumper", "powered wheelbarrow", "motorised wheelbarrow"],
  },
  "vacuum-cleaners": {
    title: "Vacuum Cleaners",
    slug: "vacuum-cleaners",
    metaTitle: `Best Vacuum Cleaner Reviews UK ${currentYear} | Expert Tested`,
    metaDescription: `Expert UK vacuum cleaner reviews. Cordless, upright, cylinder and robot vacuums tested for suction power and value.`,
    description: "Reviews of cordless, upright, cylinder and robot vacuum cleaners tested for suction power and ease of use.",
    longDescription: "From powerful upright vacuums for deep cleaning to lightweight cordless stick vacs for quick pickups, we test every vacuum for suction, filtration, noise, and usability on different floor types.",
    heroGradient: "bg-linear-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 dark:to-violet-950/20",
    articleKeywords: ["vacuum cleaner", "vacuum", "cordless vacuum", "robot vacuum"],
  },
};
